import { timezoneAbbreviation } from "@caredna-marlin/utils/dates";
import {
  useTheme,
} from "@mui/material";
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { addDays, isSameDay } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

type Props = {
  startTime: Date,
  endTime: Date,
  onChange: (start: Date, end: Date) => void,
  timezone?: string,
  mode: "datetime" | "time",
  clearable?: boolean,
  disabled?: boolean,
}

const editStyles = {
  width: "6rem",
  mr: 1,
  verticalAlign: "middle",
};

const StartEndTime = ({ startTime, endTime, onChange, timezone, mode, disabled }: Props) => {
  const theme = useTheme();

  const setTime = ( start: Date, end: Date) => {
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      onChange(startTime, endTime);
      return;
    }

    end.setFullYear(start.getFullYear(), start.getMonth(), start.getDate());
    onChange(start, end <= start ? addDays(end, 1): end);
  }

  const providerLocalStartTime = timezone ? toZonedTime(startTime, timezone) : startTime;
  const providerLocalEndTime = timezone ? toZonedTime(endTime, timezone) : endTime;

  const endTimeColor = !isSameDay(providerLocalStartTime, providerLocalEndTime) ? theme.palette.error.light : undefined;

  const { getTimeFormat, datef } = useZonedDateFormat();
  const timeFormatString = getTimeFormat(true, false);

  return <>
    <TimeField
      aria-label="Start"
      format={timeFormatString}
      value={providerLocalStartTime}
      onChange={start => start && setTime(timezone ? fromZonedTime(start, timezone) : start, endTime)}
      size="small"
      sx={editStyles}
      disabled={disabled}
      helperText={mode === "datetime" ? <DayHelperText date={datef(providerLocalStartTime, "MMM d")} /> : null}
    />
    -
    <TimeField
      aria-label="End"
      format={timeFormatString}
      value={providerLocalEndTime}
      onChange={end => end && setTime(startTime, timezone ? fromZonedTime(end, timezone) : end)}
      size="small"
      sx={{ ...editStyles, ml: 1 }}
      disabled={disabled}
      helperText={mode === "datetime" ? <DayHelperText date={datef(providerLocalEndTime, "MMM d")} color={endTimeColor} /> : (endTimeColor && <DayHelperText date="Next Day" color={endTimeColor} />)}
    />
    {timezone && timezoneAbbreviation(timezone)}
  </>
}

const DayHelperText = ({ date, color }: { date: string, color?: string }) => {
  const theme = useTheme();
  return <span style={{
    background: theme.palette.background.default,
    bottom: -5,
    color,
    position: "absolute",
    right: 8,
  }}>
    {date}
  </span>
}

export default StartEndTime;
